import { useMemo } from "react";
import { Task, TaskInstance, InstanceStatus, RecurrencePattern } from "@/lib/api/checklist";
import { getPeriodsForRecurrence, groupInstancesByPeriod, getDateRangeForRecurrence } from "@/lib/utils/recurrence";
import { Check, Clock, AlertCircle, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { getYear } from "date-fns";

interface BiannuallyBreakdownViewProps {
  tasks: Task[];
  instances: TaskInstance[];
  lookbackHalfYears: number;
  isFiscalYear?: boolean;
}

interface HalfYearData {
  year: number;
  half: string; // "H1" or "H2"
  label: string;
  periodKey: string;
}

export function BiannuallyBreakdownView({ tasks, instances, lookbackHalfYears, isFiscalYear = false }: BiannuallyBreakdownViewProps) {
  // Get date range and periods
  const { start, end } = getDateRangeForRecurrence(RecurrencePattern.BIANNUALLY, lookbackHalfYears);
  const periods = getPeriodsForRecurrence(RecurrencePattern.BIANNUALLY, start, end);
  
  // Group periods by year for display
  const halfYearsByYear = useMemo(() => {
    const grouped = new Map<number, HalfYearData[]>();
    
    periods.forEach(period => {
      const periodDate = period.start;
      let displayYear = getYear(periodDate);
      const half = period.label; // "H1" or "H2"
      
      // For fiscal year (July-June):
      // H2 (Jul-Dec) belongs to the next fiscal year
      // H1 (Jan-Jun) belongs to the current fiscal year
      if (isFiscalYear) {
        if (half === 'H2') {
          // H2 (Jul-Dec) belongs to the fiscal year ending the following June
          displayYear = displayYear + 1;
        }
        // H1 (Jan-Jun) stays with its calendar year for fiscal year
      }
      
      const halfYearData: HalfYearData = {
        year: displayYear,
        half,
        label: period.label,
        periodKey: period.key,
      };
      
      const existing = grouped.get(displayYear) || [];
      grouped.set(displayYear, [...existing, halfYearData]);
    });
    
    return Array.from(grouped.entries())
      .sort((a, b) => b[0] - a[0]) // Sort years descending (newest first)
      .map(([year, halves]) => ({
        year,
        halves: halves.sort((a, b) => {
          // Always show H2 -> H1 order (reversed like quarters)
          return b.half.localeCompare(a.half);
        }),
      }));
  }, [periods, isFiscalYear]);
  
  // Group instances by task and period
  const instancesByTaskAndPeriod = useMemo(() => {
    const grouped = new Map<string, Map<string, TaskInstance[]>>();
    
    tasks.forEach(task => {
      const taskInstances = instances.filter(inst => inst.task_id === task.id);
      const periodMap = groupInstancesByPeriod(taskInstances, periods);
      grouped.set(task.id, periodMap);
    });
    
    return grouped;
  }, [tasks, instances, periods]);
  
  // Get task deadline from first instance
  const getTaskDeadline = (task: Task): string => {
    const taskInstances = instances.filter(inst => inst.task_id === task.id);
    if (taskInstances.length > 0) {
      const firstInstance = taskInstances.sort((a, b) => 
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      )[0];
      return moment.tz(firstInstance.due_date, CommonDestinationTimezone).format("MMMM Do");
    }
    return "";
  };
  
  // Get status for a task's half year
  const getHalfYearStatus = (task: Task, periodKey: string) => {
    const taskPeriodMap = instancesByTaskAndPeriod.get(task.id);
    const periodInstances = taskPeriodMap?.get(periodKey) || [];
    
    if (periodInstances.length === 0) {
      const period = periods.find(p => p.key === periodKey);
      if (period && period.start > new Date()) {
        return { status: 'future', label: '', icon: null };
      }
      return { status: 'no-data', label: '', icon: null };
    }
    
    const completed = periodInstances.find(inst => inst.status === InstanceStatus.COMPLETED);
    const pending = periodInstances.find(inst => inst.status === InstanceStatus.PENDING);
    
    if (completed) {
      return {
        status: completed.is_late ? 'completed-late' : 'completed',
        label: completed.is_late ? 'Done Late' : 'Done',
        icon: <Check className="h-3 w-3" />,
      };
    }
    
    if (pending) {
      return {
        status: pending.is_late ? 'late' : 'pending',
        label: pending.is_late ? 'Late' : 'Pending',
        icon: pending.is_late ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />,
      };
    }
    
    return { status: 'unknown', label: '', icon: null };
  };
  
  // All years represented in the data
  const allYears = halfYearsByYear.map(({ year }) => year);
  
  // Sort tasks: those with data first, then those without
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const aInstances = instances.filter(inst => inst.task_id === a.id);
      const bInstances = instances.filter(inst => inst.task_id === b.id);
      
      // Tasks with instances come first
      if (aInstances.length > 0 && bInstances.length === 0) return -1;
      if (aInstances.length === 0 && bInstances.length > 0) return 1;
      
      // If both have instances or both don't, maintain original order
      return 0;
    });
  }, [tasks, instances]);
  
  return (
    <div className="space-y-1">
      {/* Header with year labels */}
      <div className="flex gap-1 mb-2">
        <div className="w-[280px]" /> {/* Space for task names */}
        {allYears.map(year => {
          const yearData = halfYearsByYear.find(yd => yd.year === year);
          // Always show half-years in H2 -> H1 order
          const halfLabels = ['H2', 'H1'];
          
          return (
            <div key={year} className="flex-1">
              <div className="text-center">
                <div className="text-sm font-medium text-muted-foreground">
                  <b>{isFiscalYear ? `FY${year}` : year}</b>
                </div>
                <div className="flex gap-1 mt-1">
                  {halfLabels.map((displayH) => {
                    // In fiscal year view, map display halves to actual calendar halves
                    let calendarH = displayH;
                    if (isFiscalYear) {
                      // Map fiscal halves to calendar halves:
                      // Fiscal H1 (Jul-Dec) = Calendar H2
                      // Fiscal H2 (Jan-Jun) = Calendar H1
                      switch(displayH) {
                        case 'H1': calendarH = 'H2'; break;
                        case 'H2': calendarH = 'H1'; break;
                      }
                    }
                    
                    // Check if this half exists in our data range using calendar half
                    const halfExists = yearData?.halves.some(half => half.label === calendarH);
                    
                    // Add month ranges based on display half
                    let monthRange = '';
                    switch(displayH) {
                      case 'H1': monthRange = isFiscalYear ? 'Jul-Dec' : 'Jan-Jun'; break;
                      case 'H2': monthRange = isFiscalYear ? 'Jan-Jun' : 'Jul-Dec'; break;
                    }
                    
                    return (
                      <div 
                        key={displayH} 
                        className={cn(
                          "flex-1 text-xs text-center rounded px-1",
                          halfExists 
                            ? "bg-yellow-100 text-yellow-800 font-medium" 
                            : "text-muted-foreground"
                        )}
                      >
                        <div>{displayH}</div>
                        <div className="text-[10px] opacity-80">{monthRange}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
              {isFiscalYear && (
                <div 
                  className={cn(
                    "h-1 mt-1 rounded-full",
                    // Alternate colors for fiscal years
                    (allYears.indexOf(year) % 2 === 0) 
                      ? "bg-purple-400" 
                      : "bg-teal-400"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Tasks */}
      {sortedTasks.map(task => {
        const deadline = getTaskDeadline(task);
        
        return (
          <div key={task.id} className="flex gap-1 items-center group hover:bg-muted/50 rounded-lg p-1 -m-1">
            {/* Task Info */}
            <div className="w-[280px] pr-2">
              <div className="text-sm font-medium truncate">{task.title}</div>
              {deadline && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {deadline}
                </div>
              )}
            </div>
            
            {/* Half Years by Year */}
            {allYears.map(year => {
              const yearData = halfYearsByYear.find(yd => yd.year === year);
              // Always show half-years in H2 -> H1 order
              const halfLabels = ['H2', 'H1'];
              
              return (
                <div key={year} className="flex-1 flex gap-1">
                  {halfLabels.map(displayH => {
                    // In fiscal year view, map display halves to actual calendar halves
                    let calendarH = displayH;
                    if (isFiscalYear) {
                      switch(displayH) {
                        case 'H1': calendarH = 'H2'; break;
                        case 'H2': calendarH = 'H1'; break;
                      }
                    }
                    
                    const half = yearData?.halves.find(h => h.label === calendarH);
                    
                    if (!half) {
                      // This year doesn't have this half in our date range
                      return (
                        <div 
                          key={displayH} 
                          className="flex-1 min-w-[60px] rounded px-1 py-0.5 flex items-center justify-center"
                        >
                          <div className="w-4 h-px bg-gray-300" />
                        </div>
                      );
                    }
                    
                    const { status, label, icon } = getHalfYearStatus(task, half.periodKey);
                    const period = periods.find(p => p.key === half.periodKey);
                    const isFuture = period && period.start > new Date();
                    const hasData = status !== 'no-data' && status !== 'future';
                    
                    return (
                      <div
                        key={half.periodKey}
                        className={cn(
                          "flex-1 min-w-[60px] rounded px-1 py-0.5 text-center transition-all",
                          "flex items-center justify-center gap-0.5",
                          "border-2",
                          // Border styles
                          hasData && !isFuture && "border-indigo-400",
                          !hasData && !isFuture && "border-transparent",
                          isFuture && "border-transparent opacity-50",
                          // Background styles
                          status === 'completed' && "bg-green-100 text-green-700",
                          status === 'completed-late' && "bg-orange-100 text-orange-700",
                          status === 'pending' && "bg-blue-100 text-blue-700",
                          status === 'late' && "bg-red-100 text-red-700",
                          status === 'no-data' && !isFuture && "border-gray-500 py-0.5",
                          isFuture && "bg-gray-100 text-gray-400"
                        )}
                      >
                        {icon}
                        {label && <span className="text-[10px] font-medium">{label}</span>}
                        {(!label && status === 'no-data') && <span className="text-[10px] font-medium">No Data</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
      
      {/* Compact Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-4 pt-2 border-t">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-100" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-orange-100" />
          <span>Completed Late</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-100" />
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-100" />
          <span>Late</span>
        </div>
      </div>
    </div>
  );
}
